import styled from "@emotion/styled";
import { useState } from "react";

import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import {
  CommercialPlanKey,
  formatCommercialPrice,
  type CommercialCatalogSnapshot,
  type CommercialPlanDefinition,
  type CommercialPlanSnapshot,
} from "@/modules/commercial-plans";
import { CommercialPlanSelector } from "./commercial-plan-selector";
import { CommercialPlanSelectorModal } from "./commercial-plan-selector-modal";
import { getCommercialPlanSelection } from "./plan-selection";

interface CommercialOnboardingPlanStepProps {
  actionIsLoading?: boolean;
  catalog: CommercialCatalogSnapshot;
  onChoosePlan: (plan: CommercialPlanDefinition) => void;
  onContinueFree: () => void;
  snapshot: CommercialPlanSnapshot;
}

export const CommercialOnboardingPlanStep = ({
  actionIsLoading = false,
  catalog,
  onChoosePlan,
  onContinueFree,
  snapshot,
}: CommercialOnboardingPlanStepProps) => {
  const selection = getCommercialPlanSelection({ catalog, mode: "focused", snapshot });
  const [allPlansModalIsOpen, setAllPlansModalIsOpen] = useState(false);
  const [selectedPlanKey, setSelectedPlanKey] = useState<CommercialPlanKey>(
    selection.initialSelectedPlanKey
  );
  const memProPlan = catalog.plans.find(plan => plan.key === CommercialPlanKey.MemPro);
  const isFreeTrialOffer = snapshot.memProTrialEligible === true;

  if (isFreeTrialOffer && memProPlan?.checkoutAvailable) {
    return (
      <OnboardingPlanSurface>
        <OnboardingPlanHeader>
          <MDSBadge label="7 days free" variant={MDSBadgeVariant.Emphasis} />
          <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
            Start with Mem Pro
          </MDSText>
          <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
            Try Mem Pro free for 7 days, then continue for{" "}
            {formatCommercialPrice(memProPlan.monthlyPriceCents)}/month. Cancel anytime.
          </MDSText>
        </OnboardingPlanHeader>

        <TrialPitchCard>
          <TrialPitchIntro>
            <MDSText variant={MDSTextVariant.HeaderS} weight={MDSTextWeight.SemiBold}>
              Give Mem more ways to follow through
            </MDSText>
            <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
              Explore the parts of Mem that work proactively across your notes and tools.
            </MDSText>
          </TrialPitchIntro>

          <TrialBenefits>
            <TrialBenefit>
              <BenefitIcon aria-hidden="true" kind={MDSIconKind.Link} size={15} />
              <TrialBenefitCopy>
                <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
                  Connect your tools
                </MDSText>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  Bring in context from Gmail, Slack, Todoist, and more.
                </MDSText>
              </TrialBenefitCopy>
            </TrialBenefit>
            <TrialBenefit>
              <BenefitIcon aria-hidden="true" kind={MDSIconKind.BoltOutline} size={15} />
              <TrialBenefitCopy>
                <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
                  Create Custom Routines
                </MDSText>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  Have Mem prepare, research, organize, and remind you on a schedule.
                </MDSText>
              </TrialBenefitCopy>
            </TrialBenefit>
            <TrialBenefit>
              <BenefitIcon aria-hidden="true" kind={MDSIconKind.SparkleOutline} size={15} />
              <TrialBenefitCopy>
                <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Medium}>
                  Track work proactively
                </MDSText>
                <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
                  Let Mem detect and track more tasks and projects across the notes you use.
                </MDSText>
              </TrialBenefitCopy>
            </TrialBenefit>
          </TrialBenefits>
        </TrialPitchCard>

        <TrialActions>
          <MDSButton
            variant={MDSButtonVariant.Emphasis}
            isLoading={actionIsLoading}
            onClick={() => onChoosePlan(memProPlan)}
          >
            Start 7-day free trial
          </MDSButton>
          <MDSButton onClick={onContinueFree}>Skip for now</MDSButton>
        </TrialActions>
      </OnboardingPlanSurface>
    );
  }

  return (
    <OnboardingPlanSurface>
      <OnboardingPlanHeader>
        <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
          Choose how much Mem can take on
        </MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
          Every plan keeps your notes safe. Mem Pro adds custom routines and connections so Mem can
          follow through in the background.
        </MDSText>
      </OnboardingPlanHeader>

      <CommercialPlanSelector
        actionIsLoading={actionIsLoading}
        compactCards
        currentPlanKey={snapshot.currentPlan.key}
        memProTrialIsAvailable={isFreeTrialOffer}
        onChoosePlan={onChoosePlan}
        onSelectPlan={setSelectedPlanKey}
        onShowAllPlans={() => setAllPlansModalIsOpen(true)}
        plans={selection.visiblePlans}
        recommendedPlanKey={selection.recommendedPlanKey}
        selectedPlanKey={selectedPlanKey}
      />

      <ContinueFreeButton onClick={onContinueFree}>Continue with Mem</ContinueFreeButton>

      <CommercialPlanSelectorModal
        actionIsLoading={actionIsLoading}
        catalog={catalog}
        isOpen={allPlansModalIsOpen}
        mode="all"
        onChoosePlan={onChoosePlan}
        onClose={() => setAllPlansModalIsOpen(false)}
        snapshot={snapshot}
        title="Compare Mem plans"
      />
    </OnboardingPlanSurface>
  );
};

const OnboardingPlanSurface = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[1]};
`;

const OnboardingPlanHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  text-align: center;
`;

const ContinueFreeButton = styled(MDSButton)`
  align-self: center;
`;

const TrialPitchCard = styled.section`
  background: ${({ theme }) => theme.colors.background.input.chrome.focused};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  margin: 0 auto;
  max-width: 680px;
  padding: ${({ theme }) => theme.spacing[6]};
  width: 100%;
`;

const TrialPitchIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  text-align: center;
`;

const TrialBenefits = styled.ul`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(3, minmax(0, 1fr));
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const TrialBenefit = styled.li`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const BenefitIcon = styled(MDSIcon)`
  color: ${({ theme }) => theme.colors.icon.emphasis};
  flex: 0 0 auto;
  margin-top: 2px;
`;

const TrialBenefitCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const TrialActions = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;
